/**
 * motionGrammar.js — Canonical Granullo Motion Grammar
 * 
 * State-driven, narrative motion. Zero ornamental gimmick.
 * Every animation must clarify: sequence, consequence, selection,
 * prevention, replay, trust state, or chapter transition.
 * 
 * GRANULLO_VISUAL_AUTHORITY_BASELINE_V2 — NON-REGRESSIBLE
 */

/* ═══ Easing Curves ═══ */
export const EASE = {
  out: [0.22, 1, 0.36, 1],
  inOut: [0.4, 0, 0.2, 1],
  reveal: [0.16, 1, 0.3, 1],
  spring: { type: 'spring', stiffness: 260, damping: 24 },
};

/* ═══ Durations (seconds for Framer Motion) ═══ */
export const DUR = {
  micro: 0.15,
  standard: 0.22,
  medium: 0.32,
  entrance: 0.5,
  narrative: 0.7,
  cinematic: 1.0,
};

/* ═══ Stagger Intervals (seconds) ═══ */
export const STAGGER = {
  xs: 0.04,
  sm: 0.08,
  md: 0.12,
  lg: 0.2,
};

/* ═══ Section Entrance Choreography ═══
   Staggered reveals — every fold earns the next layer.
   Usage: <motion.div {...sectionEntrance(0)}>
          <motion.h2 {...sectionEntrance(1)}>
          <motion.p {...sectionEntrance(2)}>
*/
export function sectionEntrance(index = 0, customDelay = 0) {
  return {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
    transition: {
      duration: DUR.entrance,
      delay: customDelay + index * STAGGER.md,
      ease: EASE.reveal,
    },
  };
}

/* ═══ Meta Label Entrance (uppercase labels above sections) ═══ */
export function metaEntrance(delay = 0) {
  return {
    initial: { opacity: 0, y: 8 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-60px' },
    transition: {
      duration: DUR.medium,
      delay,
      ease: EASE.reveal,
    },
  };
}

/* ═══ Card Grid Stagger ═══
   Cards lift in with staggered timing.
   Usage: <motion.div {...cardEntrance(i)}> where i = card index
*/
export function cardEntrance(index = 0) {
  return {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-40px' },
    transition: {
      duration: DUR.medium,
      delay: index * STAGGER.sm,
      ease: EASE.reveal,
    },
  };
}

/* ═══ Chapter Transition Choreography ═══
   Line draws left→right, then thesis copy drifts in.
*/
export const chapterLine = {
  initial: { scaleX: 0 },
  whileInView: { scaleX: 1 },
  viewport: { once: true, margin: '-40px' },
  transition: {
    duration: DUR.narrative,
    ease: EASE.reveal,
  },
  style: { transformOrigin: 'left center' },
};

export const chapterThesis = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: {
    duration: DUR.entrance,
    delay: 0.35,
    ease: EASE.reveal,
  },
};

/* ═══ Node Activation (Truth Graph) ═══
   Nodes pulse into sequence with staggered timing.
*/
export function nodeEntrance(index = 0) {
  return {
    initial: { opacity: 0, scale: 0.85 },
    whileInView: { opacity: 1, scale: 1 },
    viewport: { once: true, margin: '-40px' },
    transition: {
      duration: DUR.medium,
      delay: 0.1 + index * STAGGER.xs,
      ease: EASE.spring,
    },
  };
}

/* ═══ Node Selection State ═══ */
export const nodeSelect = {
  scale: 1.06,
  transition: { duration: DUR.standard, ease: EASE.out },
};

export const nodeDeselect = {
  scale: 1,
  transition: { duration: DUR.standard, ease: EASE.out },
};

/* ═══ Edge Illumination ═══ */
export const edgePulse = {
  initial: { pathLength: 0, opacity: 0 },
  animate: { pathLength: 1, opacity: 1 },
  transition: { duration: DUR.narrative, ease: EASE.reveal },
};

/* ═══ Table Row Entrance ═══ */
export function rowEntrance(index = 0) {
  return {
    initial: { opacity: 0, x: -8 },
    whileInView: { opacity: 1, x: 0 },
    viewport: { once: true, margin: '-20px' },
    transition: {
      duration: DUR.medium,
      delay: index * STAGGER.xs,
      ease: EASE.reveal,
    },
  };
}

/* ═══ Hero Theater Choreography ═══
   Layered text/panel timing for hero section.
*/
export const heroStagger = {
  line1: { delay: 0.1, duration: DUR.entrance },
  line2: { delay: 0.3, duration: DUR.entrance },
  question: { delay: 0.5, duration: DUR.entrance },
  statement: { delay: 0.8, duration: DUR.narrative },
  reinforcement: { delay: 1.1, duration: DUR.entrance },
  cta: { delay: 1.4, duration: DUR.medium },
  panel: { delay: 0.6, duration: DUR.cinematic },
};

/* ═══ Stagger Container (for Framer Motion) ═══
   Parent variant that staggers children.
*/
export const staggerContainer = (staggerDelay = STAGGER.sm) => ({
  initial: 'hidden',
  whileInView: 'visible',
  viewport: { once: true, margin: '-60px' },
  variants: {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: staggerDelay,
      },
    },
  },
});

export const staggerChild = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: DUR.medium, ease: EASE.reveal },
  },
};

/* ═══ Reduced Motion Check ═══ */
export function prefersReducedMotion() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
